<script setup lang="ts">
import { useNotificationStore } from '@/stores/notification'
import { storeToRefs } from 'pinia'

const notificationStore = useNotificationStore()
const { notifications } = storeToRefs(notificationStore)
</script>

<template>
  <div class="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
    <TransitionGroup
      name="notification"
      tag="div"
      class="flex flex-col gap-2 pointer-events-auto"
    >
      <div
        v-for="n in notifications"
        :key="n.id"
        class="flex items-start p-4 rounded-xl shadow-xl border-l-4 transition-all duration-300 bg-white"
        :class="{
          'border-emerald-500 text-gray-800 ring-1 ring-emerald-100': n.type === 'success',
          'border-red-500 text-gray-800 ring-1 ring-red-100': n.type === 'error',
          'border-amber-500 text-gray-800 ring-1 ring-amber-100': n.type === 'warning',
          'border-blue-500 text-gray-800 ring-1 ring-blue-100': n.type === 'info',
        }"
      >
        <div class="flex-shrink-0 mr-3 mt-0.5">
          <svg v-if="n.type === 'success'" class="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <svg v-else-if="n.type === 'error'" class="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <svg v-else-if="n.type === 'warning'" class="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <svg v-else class="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="flex-1 text-sm font-medium leading-snug whitespace-pre-line text-gray-800">
          {{ n.message }}
        </div>
        <button
          @click="notificationStore.remove(n.id)"
          class="ml-3 -mr-1 flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors p-1"
          aria-label="Close notification"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.notification-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.notification-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
.notification-move {
  transition: transform 0.4s ease;
}
</style>
